/**
 * personalizacao-preview.js  v4 – PNG Numbers + Font Name + Mobile Preview
 * ─────────────────────────────────────────────────────────────────────────
 * Compõe imagens PNG individuais para números e usa fonte
 * para o nome sobre a foto de costas da camisa.
 * No mobile, cria um mini preview abaixo dos campos de personalização.
 *
 * Nomenclatura dos PNGs de número em Configurações > Arquivos:
 *   kit-{set}-num-{0-9}.png     ex: kit-home-num-7.png, kit-away-num-3.png
 *
 * Tags do produto (Shopify Admin):
 *   - "personalizar-preview"           → Ativa a feature
 *   - "personalizar-kit:home"          → Usa PNGs set "home" + cor nome #003D49
 *   - "personalizar-kit:away"          → Usa PNGs set "away" + cor nome #FFFFFF
 *
 * Override de cor via tag:  "personalizar-nome-cor:#FFD700"
 *
 * Alt text da foto de costas deve conter: "costas-personalizar"
 *
 * CDR Group
 */

(() => {
  /* ───── Utilitários ───── */
  const normalize = (str) =>
    (str || "").toString().trim().toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const FEATURE_TAG = "personalizar-preview";
  const KIT_TAG_PREFIX = "personalizar-kit:";
  const COR_TAG_PREFIX = "personalizar-nome-cor:";
  const ALT_TAG = "costas-personalizar";
  /* mesma lista do personalizarCamiseta.js: valores de opcao que ligam a personalizacao */
  const VALORES_ATIVA_PERSO = ["customize", "customise", "personalizar", "personalizacao", "com personalizacao", "sim", "yes"];

  /* Cores padrão do nome por kit */
  const KIT_NAME_COLORS = {
    home: "#003D49",
    away: "#FFFFFF",
  };

  /* ───── Cache de imagens ───── */
  const imgCache = new Map();

  function preloadImg(url) {
    if (imgCache.has(url)) return imgCache.get(url);
    const p = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => { console.warn("[Preview] Falha ao carregar:", url); resolve(null); };
      img.src = url;
    });
    imgCache.set(url, p);
    return p;
  }

  /* ───── Classe principal ───── */
  class PersonalizacaoPreview {
    constructor(el) {
      this.productInfo = el;
      this.sectionId = el.dataset.section;
      this.galleryEl = el.querySelector("media-gallery");
      this.galleryList = this.galleryEl?.querySelector(
        `#Slider-Gallery-${this.sectionId}`
      );
      this.originalOrder = null;
      this.isActive = false;
      this.cfg = this._readConfig();
      this._debounceTimer = null;
      this._mobilePreview = null;
    }

    /* ── Config ── */

    _readConfig() {
      const s = this.productInfo.querySelector("script[data-personalizar-preview-config]");
      if (!s) return null;
      try { return JSON.parse(s.textContent); } catch { return null; }
    }

    static isEnabled(el) {
      const s = el.querySelector("script[data-personalizar-preview-config]");
      if (!s) return false;
      try {
        const c = JSON.parse(s.textContent);
        return (c.tags || []).some((t) => normalize(t) === normalize(FEATURE_TAG));
      } catch { return false; }
    }

    _getKit() {
      for (const tag of this.cfg?.tags || []) {
        const l = tag.toLowerCase().trim();
        if (l.startsWith(KIT_TAG_PREFIX)) return l.substring(KIT_TAG_PREFIX.length).trim();
      }
      return "home";
    }

    _getNameColor() {
      /* 1. Tag explícita de cor */
      for (const tag of this.cfg?.tags || []) {
        const l = tag.toLowerCase().trim();
        if (l.startsWith(COR_TAG_PREFIX)) return tag.substring(COR_TAG_PREFIX.length).trim();
      }
      /* 2. Cor padrão do kit */
      return KIT_NAME_COLORS[this._getKit()] || "#FFFFFF";
    }

    _numUrl(digit) {
      const base = this.cfg?.assetBase || "";
      return `${base}kit-${this._getKit()}-num-${digit}.png`;
    }

    _preloadNumbers() {
      for (let i = 0; i <= 9; i++) preloadImg(this._numUrl(i));
    }

    /* ── Ordem dos slides ── */

    _saveOrder() {
      if (this.originalOrder) return;
      this.originalOrder = Array.from(
        this.galleryList.querySelectorAll("li[data-media-id]")
      ).map((li) => li.dataset.mediaId);
    }

    _findBackSlide() {
      for (const li of this.galleryList.querySelectorAll("li[data-media-id]")) {
        const img = li.querySelector("img");
        if (img && normalize(img.alt).includes(normalize(ALT_TAG))) return li;
      }
      return null;
    }

    _reorderToBack(backSlide) {
      this.galleryList.querySelectorAll("li[data-media-id]")
        .forEach((li) => li.classList.remove("is-active"));
      this.galleryList.insertBefore(backSlide, this.galleryList.firstElementChild);
      backSlide.classList.add("is-active");
      this._syncThumbs();
      this._resetSlider();
    }

    _restoreOrder() {
      if (!this.originalOrder) return;
      const map = new Map();
      this.galleryList.querySelectorAll("li[data-media-id]")
        .forEach((li) => map.set(li.dataset.mediaId, li));
      this.originalOrder.forEach((id) => {
        const li = map.get(id);
        if (li) this.galleryList.appendChild(li);
      });
      this.galleryList.querySelectorAll("li[data-media-id]")
        .forEach((li) => li.classList.remove("is-active"));
      const first = this.galleryList.querySelector("li[data-media-id]");
      if (first) first.classList.add("is-active");
      this._syncThumbs();
      this._resetSlider();
    }

    _syncThumbs() {
      const tl = document.querySelector(`#Slider-Thumbnails-${this.sectionId}`);
      if (!tl) return;
      const order = Array.from(this.galleryList.querySelectorAll("li[data-media-id]"))
        .map((li) => li.dataset.mediaId);
      const tm = new Map();
      tl.querySelectorAll("li[data-target]").forEach((t) => tm.set(t.dataset.target, t));
      order.forEach((id) => { const t = tm.get(id); if (t) tl.appendChild(t); });
      tl.querySelectorAll("button").forEach((b) => b.removeAttribute("aria-current"));
      const fb = tl.querySelector("li[data-target] button");
      if (fb) fb.setAttribute("aria-current", "true");
    }

    _resetSlider() {
      const v = document.querySelector(`#GalleryViewer-${this.sectionId}`);
      if (!v) return;
      const s = v.querySelector(".slider");
      if (s) s.scrollTo({ left: 0, behavior: "instant" });
      const c = v.querySelector(".slider-counter--current");
      if (c) c.textContent = "1";
      if (typeof v.resetPages === "function") v.resetPages();
    }

    /* ── Overlay (galeria principal) ── */

    _getOverlay(backSlide) {
      let ov = backSlide.querySelector(".personalizacao-overlay");
      if (!ov) {
        ov = document.createElement("div");
        ov.className = "personalizacao-overlay";
        ov.innerHTML = `
          <div class="pp-overlay__nome"></div>
          <div class="pp-overlay__numero"></div>
        `;
        const ct = backSlide.querySelector(".product-media-container");
        if (ct) { ct.style.position = "relative"; ct.appendChild(ov); }
      }
      return ov;
    }

    /* ── Mobile Preview (abaixo dos campos) ── */

    _createMobilePreview(backSlide, wrapper) {
      const existing = wrapper.parentElement.querySelector('.pp-mobile-preview');
      if (existing) return existing;

      const img = backSlide.querySelector('img');
      if (!img) return null;

      const container = document.createElement('div');
      container.className = 'pp-mobile-preview';
container.innerHTML = `
        <p class="pp-mobile-preview__title">✍️ Confira a sua camisa personalizada:</p>
        <img class="pp-mobile-preview__img" src="${img.src}" alt="Preview personalização" />
        <div class="pp-mobile-overlay">
          <div class="pp-mobile-overlay__nome"></div>
          <div class="pp-mobile-overlay__numero"></div>
        </div>
      `;

      wrapper.parentElement.insertBefore(container, wrapper.nextSibling);
      return container;
    }

    _updateMobilePreview(cleanNome, cleanNum) {
      if (!this._mobilePreview) return;

      const mNome = this._mobilePreview.querySelector('.pp-mobile-overlay__nome');
      const mNum = this._mobilePreview.querySelector('.pp-mobile-overlay__numero');

      if (mNome) {
        mNome.textContent = cleanNome;
        mNome.style.color = this._getNameColor();
      }

      if (mNum) {
        mNum.innerHTML = '';
        if (cleanNum.length > 0) {
          for (const d of cleanNum.split('')) {
            const mImg = document.createElement('img');
            mImg.src = this._numUrl(d);
            mImg.alt = d;
            mImg.className = 'pp-mobile-overlay__digit';
            mImg.draggable = false;
            mNum.appendChild(mImg);
          }
        }
      }

      this._mobilePreview.style.display = '';
    }

    /* ── Render overlay (principal + mobile) ── */

    async _renderOverlay(nome, numero) {
      const backSlide = this._findBackSlide();
      if (!backSlide) return;

      const ov = this._getOverlay(backSlide);
      const nomeEl = ov.querySelector(".pp-overlay__nome");
      const numEl = ov.querySelector(".pp-overlay__numero");

      /* ── Nome (fonte) ── */
      const cleanNome = (nome || "").toUpperCase().trim();
      nomeEl.textContent = cleanNome;
      nomeEl.style.color = this._getNameColor();

      /* ── Número (PNGs) ── */
      const cleanNum = (numero || "").replace(/\D/g, "").substring(0, 3);
      numEl.innerHTML = "";

      if (cleanNum.length > 0) {
        const digits = cleanNum.split("");
        const fragment = document.createDocumentFragment();

        for (const d of digits) {
          const url = this._numUrl(d);
          const cached = await preloadImg(url);
          const img = document.createElement("img");
          img.src = url;
          img.alt = d;
          img.className = "pp-overlay__digit";
          img.draggable = false;
          if (cached) {
            img.dataset.naturalW = cached.naturalWidth;
            img.dataset.naturalH = cached.naturalHeight;
          }
          fragment.appendChild(img);
        }
        numEl.appendChild(fragment);
      }

      /* ── Mobile preview (espelho) ── */
      this._updateMobilePreview(cleanNome, cleanNum);

      /* Visibilidade */
      const hasContent = cleanNome.length > 0 || cleanNum.length > 0;
      ov.style.opacity = hasContent ? "1" : "0";
    }

    _removeOverlay() {
      this.galleryEl?.querySelectorAll(".personalizacao-overlay")
        .forEach((el) => el.remove());
      if (this._mobilePreview) this._mobilePreview.style.display = 'none';
    }

    /* ── Init ── */

    init() {
      if (!this.galleryList || !this.cfg) return;
      this._saveOrder();

      const backSlide = this._findBackSlide();
      if (!backSlide) {
        console.warn(`[PersonalizacaoPreview] Nenhuma imagem com alt "${ALT_TAG}" encontrada.`);
        return;
      }

      /* Pré-carrega PNGs dos números */
      this._preloadNumbers();

      /* Refs dos inputs */
      const wrapper = this.productInfo.querySelector(
        `[data-personalizacao-wrapper][data-section-id="${this.sectionId}"]`
      );
      const nomeInput = wrapper?.querySelector('input[name="properties[Name]"]');
      const numInput = wrapper?.querySelector('input[name="properties[Number]"]');

      /* Cria mini preview mobile */
      if (wrapper) {
        this._mobilePreview = this._createMobilePreview(backSlide, wrapper);
        if (this._mobilePreview) this._mobilePreview.style.display = 'none';
      }

      /* Debounced typing handler */
      const onType = () => {
        if (!this.isActive) return;
        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
          this._renderOverlay(nomeInput?.value, numInput?.value);
        }, 80);
      };
      nomeInput?.addEventListener("input", onType);
      numInput?.addEventListener("input", onType);

      /* CDR — quando a personalizacao vira SERVICO (sem variante), quem manda e o toggle */
      document.addEventListener("cdr:personalizacao", (ev) => {
        const on = !!(ev && ev.detail && ev.detail.ativo);
        if (on && !this.isActive) {
          this.isActive = true;
          const bs = this._findBackSlide();
          if (bs) {
            this._reorderToBack(bs);
            this._renderOverlay(nomeInput?.value, numInput?.value);
          }
        } else if (!on && this.isActive) {
          this.isActive = false;
          this._removeOverlay();
          this._restoreOrder();
        }
      });

      /* Variant change listener */
      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const d = event?.data;
          if (!d || d.sectionId !== this.sectionId) return;

          const opts = Array.isArray(d.variant?.options) ? d.variant.options : [];
          const isPers = opts.some((o) => VALORES_ATIVA_PERSO.indexOf(normalize(o)) !== -1);

          if (isPers && !this.isActive) {
            this.isActive = true;
            const bs = this._findBackSlide();
            if (bs) {
              this._reorderToBack(bs);
              this._renderOverlay(nomeInput?.value, numInput?.value);
            }
          } else if (!isPers && this.isActive) {
            this.isActive = false;
            this._removeOverlay();
            this._restoreOrder();
          }
        });
      }
    }
  }

  /* ───── Bootstrap ───── */
  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("product-info").forEach((el) => {
      if (PersonalizacaoPreview.isEnabled(el)) {
        new PersonalizacaoPreview(el).init();
      }
    });
  });
})();
